import type {
  AccountPrefs,
  ApiError,
  BookingConfirmation,
  BookingRequest,
  BookingRules,
  BookingTotal,
  Job,
  JobSummary,
  OfferRefreshRequest,
  OfferRefreshResult,
  Passenger,
  PassengerInput,
  PaymentMethod,
  ProxyEntry,
  ProxySettings,
  ProxyStateResponse,
  ProxyType,
  RelayDeployInput,
  RelayPlatform,
  SearchRequestBody,
  WafStatus,
  WalletBalance,
  WalletEntry,
} from '../../../src/types.ts';

export type { WafStatus };

export class ApiRequestError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly requiresLogin: boolean,
    readonly detail?: unknown,
  ) {
    super(message);
    this.name = 'ApiRequestError';
  }
}

/** A list plus the total etravel reported in `x-total-count`, when it did. */
export interface Paged<T> {
  items: T[];
  total: number | null;
}

async function parseError(res: Response): Promise<never> {
  const body: unknown = await res.json().catch(() => null);
  const err = (body ?? {}) as Partial<ApiError> & { detail?: unknown; title?: string; message?: string };
  // etravel's own problem+json (proxied straight through) uses title/detail.
  const msg = err.error || (typeof err.detail === 'string' && err.detail) || err.title || err.message || `HTTP ${res.status}`;
  throw new ApiRequestError(String(msg), res.status, Boolean(err.requiresLogin) || res.status === 401, err.detail ?? body);
}

async function request<T>(url: string, init?: RequestInit): Promise<T> {
  const res = await fetch(url, init);
  if (!res.ok) return parseError(res);
  const text = await res.text();
  if (!text) return undefined as T;
  try {
    return JSON.parse(text) as T;
  } catch {
    return text as unknown as T;
  }
}

async function requestPaged<T>(url: string, init?: RequestInit): Promise<Paged<T>> {
  const res = await fetch(url, init);
  if (!res.ok) return parseError(res);
  const items = (await res.json().catch(() => [])) as T[];
  const total = Number(res.headers.get('x-total-count'));
  return { items: Array.isArray(items) ? items : [], total: Number.isFinite(total) ? total : null };
}

/** Build the RequestInit for a JSON body — the URL stays with the caller. */
function jsonBody(data: unknown, method = 'POST'): RequestInit {
  return { method, headers: { 'content-type': 'application/json' }, body: JSON.stringify(data) };
}

function qs(params: Record<string, string | number | boolean | null | undefined>): string {
  const p = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) if (v !== undefined && v !== null && v !== '') p.set(k, String(v));
  const s = p.toString();
  return s ? `?${s}` : '';
}

/** `/api/etravel/<path>` — any app.etravel.ly account endpoint, through our backend. */
const et = (path: string) => `/api/etravel/${path.replace(/^\/+/, '')}`;

export interface AuthStatus {
  loggedIn: boolean;
  email?: string | null;
  expiryDate?: string | null;
  /** True when this account may open the proxy settings (server's ADMINS). */
  proxyAdmin?: boolean;
}

export interface ProxyTestResult {
  ok: boolean;
  status: number;
  latencyMs: number;
  error?: string;
}

/** A saved flight booking as etravel returns it — one record per passenger, grouped by PNR. */
export interface FlightBooking {
  id: number;
  pnr?: string | null;
  bookingType?: string | null;
  etravelReference?: string | null;
  gdsType?: string | null;
  flightType?: string | null;
  passengerFirstName?: string | null;
  passengerLastName?: string | null;
  passengerPassportNo?: string | null;
  eTicketNo?: string | null;
  paxType?: string | null;
  paxNo?: number | null;
  paxTotal?: number | null;
  total?: number | null;
  currency?: string | null;
  createdDate?: string | null;
  issuedAt?: string | null;
  initialBookingExpiration?: string | null;
  itineraries?: Array<Record<string, unknown>>;
  passenger?: { id?: number; firstName?: string; lastName?: string; passportNo?: string } | null;
  [key: string]: unknown;
}

export interface Transaction {
  id: number;
  transactionType?: string;
  transactionStatus?: string;
  paymentType?: string;
  amount?: number;
  total?: number;
  currency?: string;
  transactionReference?: string;
  createdDate?: string;
  notes?: string | null;
  [key: string]: unknown;
}

export interface CustomerProfile {
  id?: number;
  name?: string;
  email?: string;
  mobileNo?: string;
  phone?: string;
  currency?: string;
  walletPublicKey?: string;
  customerType?: string;
  [key: string]: unknown;
}

export interface Country {
  id?: number;
  nameEn?: string;
  nameAr?: string;
  iso2?: string;
  iso3?: string;
  menuOrder?: number;
}

export const api = {
  searchAirports: (q: string) => request<unknown>(`/api/airports?q=${encodeURIComponent(q)}`),
  defaultAirports: () => request<unknown>('/api/airports/default'),
  startSearch: (body: SearchRequestBody) => request<{ jobId: string }>('/api/search', jsonBody(body)),
  getJob: (id: string) => request<Job>(`/api/search/${encodeURIComponent(id)}`),
  /** Stop a running search; the days already fetched stay on screen. */
  cancelSearch: (id: string) => request<Job>(`/api/search/${encodeURIComponent(id)}/cancel`, { method: 'POST' }),
  /** Past searches of this account, newest first. */
  listSearches: () => request<JobSummary[]>('/api/searches'),
  deleteSearch: (id: string) => request<{ ok: true }>(`/api/search/${encodeURIComponent(id)}`, { method: 'DELETE' }),

  /** Airports and form defaults this account has used before. */
  prefs: () => request<AccountPrefs>('/api/prefs'),

  // Live WAF rate-limit state, for the retry banner.
  waf: () => request<WafStatus>('/api/waf'),

  // Account auth against the etravel mobile API (proxied through our backend).
  authStatus: () => request<AuthStatus>('/api/auth/status'),
  login: (username: string, password: string) =>
    request<{ ok: true; email: string; expiryDate: string }>('/api/auth/login', jsonBody({ username, password })),
  logout: () => request<{ ok: true }>('/api/auth/logout', { method: 'DELETE' }),

  // ── Booking ───────────────────────────────────────────────────────────────
  /** Re-price a fare from a month search and get a handle to book it with. */
  refreshOffer: (body: OfferRefreshRequest & { previousPrice?: number }) => request<OfferRefreshResult>('/api/offers/refresh', jsonBody(body)),
  bookingTotal: (body: BookingRequest) => request<BookingTotal>('/api/booking/total', jsonBody(body)),
  bookingRules: (body: BookingRequest) => request<BookingRules>('/api/booking/rules', jsonBody(body)),
  confirmBooking: (body: BookingRequest) => request<BookingConfirmation>('/api/booking/confirm', jsonBody(body)),

  /** Bookings, newest first. etravel keeps one record per passenger; group by PNR to show trips. */
  bookings: (page = 0, size = 100) => requestPaged<FlightBooking>(et(`flight-bookings${qs({ page, size, sort: 'createdDate,desc' })}`)),
  booking: (id: number) => request<FlightBooking>(et(`flight-bookings/${id}`)),
  bookingGroup: (pnr: string) => requestPaged<FlightBooking>(et(`flight-bookings${qs({ 'pnr.equals': pnr, size: 50 })}`)),
  cancelDetails: (id: number) => request<BookingTotal | Record<string, unknown>>(et(`flight-bookings/cancel-booking-details/${id}`)),
  cancelBooking: (id: number) => request<unknown>(et(`flight-bookings/cancel/${id}`)),
  confirmInitialDetails: (id: number) => request<BookingTotal | Record<string, unknown>>(et(`flight-bookings/confirm-initial-booking-details/${id}`)),
  confirmInitial: (id: number) => request<unknown>(et(`flight-bookings/confirm-initial-booking/${id}`)),
  sendTicketEmail: (etravelReference: string, email: string) =>
    request<unknown>(et('flight-bookings/send-ticket-email'), jsonBody({ etravelReference, email })),
  invoiceUrl: (etravelReference: string) => et(`public/flight-bookings/invoice/${encodeURIComponent(etravelReference)}`),

  // ── Passengers ────────────────────────────────────────────────────────────
  passengers: (query = '') =>
    requestPaged<Passenger>(
      et(
        `passengers${qs({
          size: 200,
          sort: 'lastName,asc',
          ...(query ? { 'firstName.contains': query, 'lastName.contains': query, 'passportNo.contains': query, or: true } : {}),
        })}`,
      ),
    ),
  createPassenger: (body: PassengerInput) => request<Passenger>(et('passengers'), jsonBody(body)),
  updatePassenger: (id: number, body: PassengerInput) => request<Passenger>(et(`passengers/${id}`), jsonBody({ ...body, id }, 'PATCH')),
  deletePassenger: (id: number) => request<unknown>(et(`passengers/${id}`), { method: 'DELETE' }),
  countries: () => request<Country[]>(et('public/countries?size=100000&sort=menuOrder,asc')),

  // ── Wallet ────────────────────────────────────────────────────────────────
  walletBalance: () => request<WalletBalance>(et('customer-wallets/balance')),
  walletHistory: (page = 0, size = 50) => requestPaged<WalletEntry>(et(`customer-wallets${qs({ page, size, sort: 'createdDate,desc' })}`)),
  transactions: (page = 0, size = 50) => requestPaged<Transaction>(et(`transactions${qs({ page, size, sort: 'createdDate,desc' })}`)),
  transactionStatus: (transactionReference: string) => request<Transaction>(et('transactions/status'), jsonBody({ transactionReference })),
  profile: () => request<CustomerProfile>(et('customers/get-profile')),
  /** Who a mobile number belongs to — the first step of a transfer. */
  customerByMobile: (mobile: string) => request<{ id: number; name?: string; [k: string]: unknown }>(et(`customers/by-mobile/${encodeURIComponent(mobile)}`)),
  sendOtp: () => request<unknown>(et('activation/customer/send-otp'), { method: 'POST' }),
  transferFees: (toCustomerId: number, amount: number) =>
    request<Array<{ amount: number; currency: string; description?: string; descriptionEn?: string }>>(
      et(`customer-wallets/transfer-fees${qs({ amount, toCustomerId })}`),
      { method: 'POST' },
    ),
  transfer: (toCustomerId: number, amount: number, otp: string) =>
    request<unknown>(et(`customer-wallets/transfer${qs({ toCustomerId, amount, otp })}`), { method: 'POST' }),

  // ── Add funds ─────────────────────────────────────────────────────────────
  paymentMethods: () => request<PaymentMethod[]>(et('public/payment-methods?size=99999999&sort=menuOrder,asc')),
  /** Generic payment-provider call: `payment-methods/<action>` with a JSON body. */
  paymentCall: <T = Record<string, unknown>>(action: string, body: unknown) => request<T>(et(`payment-methods/${action}`), jsonBody(body)),
  /** Bank-transfer receipt upload for LyPay top-ups (multipart). */
  lyPayInit: (amount: number, file: File) => {
    const fd = new FormData();
    fd.append('amount', String(amount));
    fd.append('file', file, file.name);
    return request<unknown>(et('payment-methods/ly-pay-init'), { method: 'POST', body: fd });
  },

  // ── Outbound proxy pool (admin only) ──────────────────────────────────────
  proxies: () => request<ProxyStateResponse>('/api/proxies'),
  addProxy: (body: { url: string; name?: string; type?: ProxyType }) => request<ProxyEntry>('/api/proxies', jsonBody(body)),
  updateProxy: (id: string, body: { name?: string; isActive?: boolean }) =>
    request<ProxyEntry>(`/api/proxies/${encodeURIComponent(id)}`, jsonBody(body, 'PATCH')),
  deleteProxy: (id: string) => request<{ ok: true }>(`/api/proxies/${encodeURIComponent(id)}`, { method: 'DELETE' }),
  testProxy: (id: string) =>
    request<{ result: ProxyTestResult; proxy: ProxyEntry }>(`/api/proxies/${encodeURIComponent(id)}/test`, { method: 'POST' }),
  proxySettings: (body: Partial<ProxySettings>) => request<ProxySettings>('/api/proxies', jsonBody(body, 'PUT')),
  deployRelay: (platform: RelayPlatform, body: RelayDeployInput) =>
    request<{ proxy: ProxyEntry }>(`/api/proxies/deploy/${platform}`, jsonBody(body)),

  /**
   * Call any etravel account endpoint through the backend proxy. `path` is what
   * follows `/api/` on app.etravel.ly, e.g. `customers/get-profile`.
   */
  etravel: <T>(path: string, init?: RequestInit) => request<T>(et(path), init),
};

/** Human message for a failed call, and whether it was really "please sign in". */
export function errorText(err: unknown): string {
  if (err instanceof ApiRequestError) return err.message;
  return err instanceof Error ? err.message : String(err);
}
